import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ENVIRONMENT_CONDITION_OPTIONS,
  VEHICLE_DETAIL_FIELDS,
  formQuestionTabLabel,
  type EnvironmentalConditionDraft,
  type FieldParameterDraftState,
  type FieldParameterInput,
  type FormAnswerDraftState,
  type SamplingFormGroup,
} from '../../../api/formSampling';
import { ChemParameterLabel } from '../../../components/ChemInline';
import { LHU_GROUPED_NAME_OPTIONS, type LhuGroupedName } from '../../../api/strapiCrm';
import { strapiMediaUrl } from '../../../api/strapi';
import { SurveyFormQuestionFields } from './SurveyFormQuestionFields';
import { SurveyGpsHemisphereResult } from './SurveyGpsHemisphereResult';
import {
  finishFieldMeasurementSpecs,
  isWeatherConditionOption,
  isWindDirectionOption,
  WEATHER_CONDITION_OPTIONS,
  WIND_DIRECTION_OPTIONS,
  type FinishDraft,
  type SamplingAirGroup,
} from './surveyBeritaAcaraFinishTypes';

type Props = {
  formGroups: SamplingFormGroup[];
  activeTab: number;
  onActiveTabChange: (tab: number) => void;
  airGroup: SamplingAirGroup;
  finishDraft: FinishDraft;
  onFinishDraftChange: React.Dispatch<React.SetStateAction<FinishDraft>>;
  formAnswerDraft: FormAnswerDraftState;
  onFormAnswerDraftChange: (next: FormAnswerDraftState) => void;
  fieldParameters: FieldParameterInput[];
  fieldParameterDraft: FieldParameterDraftState;
  onFieldParameterDraftChange: (next: FieldParameterDraftState) => void;
  showEnvironmentalCondition: boolean;
  environmentalConditionDraft: EnvironmentalConditionDraft[];
  onEnvironmentalConditionDraftChange: (next: EnvironmentalConditionDraft[]) => void;
  formAnswerFiles?: Record<string, File>;
  onFormAnswerFilesChange?: (next: Record<string, File>) => void;
  onSketchFileChange?: (file: File) => void;
  gpsLoading?: boolean;
  gpsDisabled?: boolean;
  onRetakeGps?: () => void;
  readOnly?: boolean;
};

export function SurveyBeritaAcaraFormTabs({
  formGroups,
  activeTab,
  onActiveTabChange,
  airGroup,
  finishDraft,
  onFinishDraftChange: setFinishDraft,
  formAnswerDraft,
  onFormAnswerDraftChange,
  fieldParameters,
  fieldParameterDraft,
  onFieldParameterDraftChange,
  showEnvironmentalCondition,
  environmentalConditionDraft,
  onEnvironmentalConditionDraftChange,
  formAnswerFiles,
  onFormAnswerFilesChange,
  onSketchFileChange,
  gpsLoading = false,
  gpsDisabled = false,
  onRetakeGps,
  readOnly = false,
}: Props) {
  const measurementFields = finishFieldMeasurementSpecs(airGroup);
  const hasSavedGps = Boolean(finishDraft.longitude.trim() && finishDraft.latitude.trim());
  const hasFinishPhoto = Boolean(finishDraft.location_sketsa_picture.trim());
  const showFieldParameterTab = fieldParameters.length > 0;
  const showEnvironmentalConditionTab = showEnvironmentalCondition;
  const showVehicleDetailTab = finishDraft.lhu_grouped_name === 'emisi_kendaraan';
  let nextTabIndex = 1;
  const vehicleDetailTabIndex = showVehicleDetailTab ? nextTabIndex++ : -1;
  const parameterLapanganTabIndex = nextTabIndex++;
  const fieldParameterTabIndex = showFieldParameterTab ? nextTabIndex++ : -1;
  const environmentalTabIndex = showEnvironmentalConditionTab ? nextTabIndex++ : -1;
  const formGroupOffset = nextTabIndex;
  const maxTab = formGroupOffset - 1 + formGroups.length;

  return (
    <Box sx={{ mb: 1 }}>
      <Tabs
        value={Math.min(activeTab, maxTab)}
        onChange={(_, v) => onActiveTabChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Detail" value={0} data-testid="survey-finish-form-tab-master" />
        {showVehicleDetailTab ? (
          <Tab
            label="Detail Kendaraan"
            value={vehicleDetailTabIndex}
            data-testid="survey-finish-form-tab-detail-kendaraan"
          />
        ) : null}
        <Tab
          label="Parameter lapangan"
          value={parameterLapanganTabIndex}
          data-testid="survey-finish-form-tab-parameter-lapangan"
        />
        {showFieldParameterTab ? (
          <Tab
            label="Parameter utama lapangan"
            value={fieldParameterTabIndex}
            data-testid="survey-finish-form-tab-parameter-utama-lapangan"
          />
        ) : null}
        {showEnvironmentalConditionTab ? (
          <Tab
            label="Environment condition"
            value={environmentalTabIndex}
            data-testid="survey-finish-form-tab-environment-condition"
          />
        ) : null}
        {formGroups.map((group, idx) => (
          <Tab
            key={group.form_question_name}
            label={formQuestionTabLabel(group.form_question_name)}
            value={idx + formGroupOffset}
            data-testid={`survey-finish-form-tab-${idx}`}
          />
        ))}
      </Tabs>

      {activeTab === 0 ? (
        <Stack spacing={2}>
          <TextField
            label="Nama lokasi"
            required={!readOnly}
            fullWidth
            size="small"
            inputProps={{ 'data-testid': 'survey-berita-acara-finish-location-name', readOnly }}
            value={finishDraft.location_name}
            onChange={(e) => setFinishDraft((d) => ({ ...d, location_name: e.target.value }))}
          />
          <TextField
            select
            label="Grup LHU"
            required={!readOnly}
            fullWidth
            size="small"
            data-testid="survey-berita-acara-finish-lhu-group"
            SelectProps={{ readOnly }}
            value={finishDraft.lhu_grouped_name}
            onChange={(e) =>
              setFinishDraft((d) => ({ ...d, lhu_grouped_name: e.target.value as LhuGroupedName }))
            }
          >
            <MenuItem value="">
              <em>Pilih grup LHU</em>
            </MenuItem>
            {LHU_GROUPED_NAME_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Catatan abnormalitas"
            fullWidth
            size="small"
            multiline
            minRows={2}
            inputProps={{ readOnly }}
            value={finishDraft.abnormalitas_note}
            onChange={(e) => setFinishDraft((d) => ({ ...d, abnormalitas_note: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={finishDraft.is_having_blanko_wadah_penyerap}
                disabled={readOnly}
                onChange={(e) =>
                  setFinishDraft((d) => ({
                    ...d,
                    is_having_blanko_wadah_penyerap: e.target.checked,
                  }))
                }
              />
            }
            label="Memiliki blanko wadah penyerap"
          />
          <Stack spacing={1}>
            {readOnly ? (
              <Typography variant="subtitle2">Foto</Typography>
            ) : (
              <>
                <FormHelperText sx={{ mx: 0 }}>
                  Syarat foto: format landscape 4:3.
                </FormHelperText>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {hasFinishPhoto ? 'Ganti file' : 'Pilih file'}
                  <input
                    type="file"
                    hidden
                    aria-label="Foto"
                    data-testid="survey-berita-acara-finish-sketsa"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      const url = URL.createObjectURL(f);
                      onSketchFileChange?.(f);
                      setFinishDraft((d) => {
                        if (d.location_sketsa_picture.startsWith('blob:')) {
                          URL.revokeObjectURL(d.location_sketsa_picture);
                        }
                        return { ...d, location_sketsa_picture: url };
                      });
                    }}
                  />
                </Button>
              </>
            )}
            {finishDraft.location_sketsa_picture ? (
              <Box
                component="img"
                src={strapiMediaUrl(finishDraft.location_sketsa_picture)}
                alt="Foto"
                data-testid="survey-berita-acara-finish-sketsa-preview"
                sx={{
                  maxWidth: 280,
                  maxHeight: 180,
                  objectFit: 'contain',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
            ) : readOnly ? (
              <Typography variant="body2" color="text.secondary">
                Belum ada foto
              </Typography>
            ) : null}
            <TextField
              label="Keterangan foto"
              required={!readOnly && !hasFinishPhoto}
              fullWidth
              size="small"
              multiline
              minRows={2}
              helperText={
                readOnly
                  ? undefined
                  : hasFinishPhoto
                    ? 'Opsional karena foto sudah ada.'
                    : 'Wajib diisi jika tidak ada foto, saat Simpan dan selesai.'
              }
              inputProps={{ 'data-testid': 'survey-berita-acara-finish-keterangan-foto', readOnly }}
              value={finishDraft.keterangan_foto}
              onChange={(e) => setFinishDraft((d) => ({ ...d, keterangan_foto: e.target.value }))}
            />
          </Stack>
          <Stack spacing={1} useFlexGap>
            <Typography variant="subtitle2">GPS</Typography>
            {readOnly ? null : (
              <Button
                variant="outlined"
                size="small"
                disabled={gpsLoading || gpsDisabled}
                onClick={() => onRetakeGps?.()}
                data-testid="survey-berita-acara-finish-retake-gps"
                sx={{ alignSelf: 'flex-start', mb: '20px' }}
              >
                {gpsLoading ? 'Mengambil GPS…' : hasSavedGps ? 'Re-take GPS' : 'Ambil GPS'}
              </Button>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Longitude"
                size="small"
                fullWidth
                disabled
                value={finishDraft.longitude}
                inputProps={{ 'data-testid': 'survey-berita-acara-finish-longitude' }}
              />
              <TextField
                label="Latitude"
                size="small"
                fullWidth
                disabled
                value={finishDraft.latitude}
                inputProps={{ 'data-testid': 'survey-berita-acara-finish-latitude' }}
              />
            </Stack>
            <SurveyGpsHemisphereResult
              longitudeText={finishDraft.longitude}
              latitudeText={finishDraft.latitude}
            />
          </Stack>
        </Stack>
      ) : showVehicleDetailTab && activeTab === vehicleDetailTabIndex ? (
        <Stack spacing={2} data-testid="survey-finish-detail-kendaraan">
          {VEHICLE_DETAIL_FIELDS.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              fullWidth
              size="small"
              inputProps={{ 'data-testid': field.testId, readOnly }}
              value={finishDraft.vehicle_information[field.key]}
              onChange={(e) =>
                setFinishDraft((d) => ({
                  ...d,
                  vehicle_information: { ...d.vehicle_information, [field.key]: e.target.value },
                }))
              }
            />
          ))}
        </Stack>
      ) : activeTab === parameterLapanganTabIndex ? (
        <Stack spacing={2} data-testid="survey-finish-parameter-lapangan">
          {measurementFields.map((field) =>
            field.key === 'weather_condition' ? (
              <TextField
                key={field.key}
                select
                label={field.label}
                required={!readOnly}
                fullWidth
                size="small"
                data-testid={field.testId}
                SelectProps={{ readOnly }}
                value={
                  isWeatherConditionOption(finishDraft.weather_condition)
                    ? finishDraft.weather_condition
                    : ''
                }
                onChange={(e) =>
                  setFinishDraft((d) => ({ ...d, weather_condition: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Pilih cuaca</em>
                </MenuItem>
                {WEATHER_CONDITION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            ) : field.key === 'wind_direction_condition' ? (
              <TextField
                key={field.key}
                select
                label={field.label}
                required={!readOnly}
                fullWidth
                size="small"
                data-testid={field.testId}
                SelectProps={{ readOnly }}
                value={
                  isWindDirectionOption(finishDraft.wind_direction_condition)
                    ? finishDraft.wind_direction_condition
                    : ''
                }
                onChange={(e) =>
                  setFinishDraft((d) => ({ ...d, wind_direction_condition: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>Pilih arah angin</em>
                </MenuItem>
                {WIND_DIRECTION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                key={field.key}
                label={field.label}
                fullWidth
                size="small"
                inputProps={{ 'data-testid': field.testId, readOnly }}
                value={finishDraft[field.key]}
                onChange={(e) => setFinishDraft((d) => ({ ...d, [field.key]: e.target.value }))}
              />
            ),
          )}
          {airGroup === 'air' ? (
            <TextField
              label="Jumlah sampel"
              fullWidth
              size="small"
              inputProps={{ 'data-testid': 'survey-berita-acara-finish-jumlah-sampel', readOnly }}
              value={finishDraft.jumlah_sampel}
              onChange={(e) => setFinishDraft((d) => ({ ...d, jumlah_sampel: e.target.value }))}
            />
          ) : null}
        </Stack>
      ) : showFieldParameterTab && activeTab === fieldParameterTabIndex ? (
        <Stack spacing={2} data-testid="survey-finish-parameter-utama-lapangan">
          {fieldParameters.map((param) => (
            <TextField
              key={param.documentId}
              label={<ChemParameterLabel nama={param.nama_parameter} simbol={param.simbol} />}
              fullWidth
              size="small"
              inputProps={{
                'data-testid': `survey-finish-parameter-utama-${param.documentId}`,
                readOnly,
              }}
              value={fieldParameterDraft[param.documentId] ?? ''}
              onChange={(e) =>
                onFieldParameterDraftChange({
                  ...fieldParameterDraft,
                  [param.documentId]: e.target.value,
                })
              }
            />
          ))}
        </Stack>
      ) : showEnvironmentalConditionTab && activeTab === environmentalTabIndex ? (
        <Stack spacing={2} data-testid="survey-finish-environment-condition">
          {environmentalConditionDraft.map((row, index) => (
            <Stack
              key={`${row.nama_parameter}-${index}`}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'flex-start' }}
            >
              <TextField
                select
                label="Nama parameter"
                size="small"
                sx={{ minWidth: { sm: 240 }, flex: 1 }}
                SelectProps={{ readOnly }}
                value={
                  (ENVIRONMENT_CONDITION_OPTIONS as readonly string[]).includes(row.nama_parameter)
                    ? row.nama_parameter
                    : ''
                }
                onChange={(e) =>
                  onEnvironmentalConditionDraftChange(
                    environmentalConditionDraft.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, nama_parameter: e.target.value } : item,
                    ),
                  )
                }
              >
                <MenuItem value="">
                  <em>Pilih</em>
                </MenuItem>
                {ENVIRONMENT_CONDITION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Unit"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{
                  'data-testid': `survey-finish-environment-unit-${index}`,
                  readOnly,
                }}
                value={row.unit}
                onChange={(e) =>
                  onEnvironmentalConditionDraftChange(
                    environmentalConditionDraft.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, unit: e.target.value } : item,
                    ),
                  )
                }
              />
              <TextField
                label="Result"
                size="small"
                sx={{ flex: 1 }}
                inputProps={{
                  'data-testid': `survey-finish-environment-result-${index}`,
                  readOnly,
                }}
                value={row.result}
                onChange={(e) =>
                  onEnvironmentalConditionDraftChange(
                    environmentalConditionDraft.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, result: e.target.value } : item,
                    ),
                  )
                }
              />
              {readOnly ? null : (
                <Button
                  size="small"
                  color="inherit"
                  data-testid={`survey-finish-environment-remove-${index}`}
                  onClick={() =>
                    onEnvironmentalConditionDraftChange(
                      environmentalConditionDraft.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                  sx={{ mt: { sm: 0.5 } }}
                >
                  Hapus
                </Button>
              )}
            </Stack>
          ))}
          {readOnly ? null : (
            <Button
              variant="outlined"
              size="small"
              data-testid="survey-finish-environment-add"
              onClick={() =>
                onEnvironmentalConditionDraftChange([
                  ...environmentalConditionDraft,
                  { nama_parameter: '', unit: '', result: '' },
                ])
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              Tambah
            </Button>
          )}
        </Stack>
      ) : formGroups[activeTab - formGroupOffset] ? (
        <SurveyFormQuestionFields
          key={formGroups[activeTab - formGroupOffset]!.form_question_name}
          group={formGroups[activeTab - formGroupOffset]!}
          draft={formAnswerDraft}
          onDraftChange={onFormAnswerDraftChange}
          pendingFiles={formAnswerFiles}
          onPendingFilesChange={onFormAnswerFilesChange}
          readOnly={readOnly}
        />
      ) : null}
    </Box>
  );
}
